import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, X, ChevronLeft, Zap, Mail, Users, Shield } from 'lucide-react';
import { seoPages } from '../data/seoPages';
import FooterTrust from '../components/FooterTrust';
import NotFound from '../components/NotFound';

const BOT_LINK = "https://t.me/instant_email_bot";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/dbwmuy0k_A9358B2D-0233-4058-88E9-3B869B36D815.png";

const CATEGORY_IMAGES = {
  "Security / CCTV": {
    url: "https://images.pexels.com/photos/36852946/pexels-photo-36852946.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Surveillance cameras mounted outdoors for security monitoring",
  },
  "Hotel / Homestay": {
    url: "https://images.pexels.com/photos/7820321/pexels-photo-7820321.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Hotel receptionist handing key card to guest at front desk",
  },
  "Business / Sales": {
    url: "https://images.unsplash.com/photo-1754531976828-69e42ce4e0d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxzYWxlcyUyMHRlYW0lMjBidXNpbmVzcyUyMGxlYWRzJTIwQ1JNfGVufDB8fHx8MTc3NjczMjM5Mnww&ixlib=rb-4.1.0&q=85",
    alt: "Professional sales team collaborating around a table",
  },
  "Finance": {
    url: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwZmluYW5jZSUyMGRhc2hib2FyZCUyMGFjY291bnRpbmclMjBsYXB0b3B8ZW58MHx8fHwxNzc2NzMyNDEyfDA&ixlib=rb-4.1.0&q=85",
    alt: "Finance dashboard and accounting analytics on laptop",
  },
  "IT / Tech": {
    url: "https://images.pexels.com/photos/4597280/pexels-photo-4597280.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Modern data center with server racks and monitoring equipment",
  },
  "Education / Others": {
    url: "https://images.pexels.com/photos/7845047/pexels-photo-7845047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Professional checking mobile notifications at office workstation",
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

function trackCTA(action) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, { event_category: 'SEO_CTA' });
  }
}

function TelegramIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  );
}

export default function SeoPage() {
  const { slug } = useParams();
  const page = seoPages.find(p => p.slug === slug);

  if (!page) return <NotFound backTo="/use-cases" backLabel="Browse all use cases" />;

  // Related pages from same category
  const related = seoPages.filter(p => p.category === page.category && p.slug !== page.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* SEO Meta */}
      <SEOMeta page={page} />

      {/* Header */}
      <header className="bg-white border-b border-gray-200/60 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="seo-logo-link">
            <img src={LOGO_URL} alt="TelegramEmailBot" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-outfit font-bold text-lg text-gray-900">TelegramEmailBot</span>
          </Link>
          <a
            href={BOT_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTA(`seo_header_${page.slug}`)}
            className="font-manrope text-sm font-semibold bg-[#0088CC] text-white px-5 py-2 rounded-full hover:bg-[#006699] transition-all shadow-md"
            data-testid="seo-header-cta"
          >
            Get Started ($4.99)
          </a>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <nav className="flex items-center gap-2 text-sm font-manrope text-gray-400" data-testid="seo-breadcrumb">
          <Link to="/" className="hover:text-[#0088CC] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/use-cases" className="hover:text-[#0088CC] transition-colors">Use Cases</Link>
          <span>/</span>
          <span className="text-gray-600">{page.category}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="pt-10 pb-16 sm:pt-14 sm:pb-20 px-4" data-testid="seo-hero">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-[#0088CC]/10 text-[#0088CC] font-manrope font-semibold text-xs tracking-widest uppercase px-4 py-1.5 rounded-full mb-5">
              <Zap className="w-3.5 h-3.5" /> {page.category}
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-gray-900 leading-[1.1] mb-5">
              {page.headline}
            </motion.h1>
            <motion.p variants={fadeUp} className="font-manrope text-base sm:text-lg text-gray-500 max-w-2xl mb-8 leading-relaxed">
              {page.subheadline}
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <a
                href={BOT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCTA(`seo_hero_${page.slug}`)}
                className="inline-flex items-center gap-2.5 bg-[#0088CC] text-white font-manrope font-bold px-8 py-3.5 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg"
                data-testid="seo-hero-cta"
              >
                <TelegramIcon className="w-5 h-5" /> Get Started ($4.99)
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 font-manrope font-semibold px-8 py-3.5 rounded-full hover:border-gray-400 hover:bg-gray-50 transition-all"
              >
                See How It Works <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
            {/* Category Hero Image */}
            {CATEGORY_IMAGES[page.category] && (
              <motion.div variants={fadeUp} className="mt-10">
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-200/60">
                  <img
                    src={CATEGORY_IMAGES[page.category].url}
                    alt={CATEGORY_IMAGES[page.category].alt}
                    className="w-full h-48 sm:h-64 lg:h-80 object-cover"
                    loading="eager"
                    fetchpriority="high"
                    width="1200"
                    height="400"
                    data-testid="seo-hero-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-gray-800 font-manrope text-xs font-semibold px-3 py-1.5 rounded-full">
                      <Zap className="w-3 h-3 text-[#0088CC]" /> {page.category}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Pain vs Solution */}
      <section className="py-16 sm:py-20 px-4 bg-[#F8F9FA]" data-testid="seo-pain-solution">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-outfit font-bold text-2xl sm:text-3xl text-gray-900 text-center mb-10">The Problem & The Solution</motion.h2>
            <div className="space-y-3">
              {page.painPoints.map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="grid grid-cols-2 gap-3 sm:gap-6">
                  <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3.5 flex items-center gap-3">
                    <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="font-manrope text-sm text-red-700">{item.problem}</span>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3.5 flex items-center gap-3">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="font-manrope text-sm text-emerald-800">{item.solution}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 sm:py-20 px-4" data-testid="seo-how-it-works">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-outfit font-bold text-2xl sm:text-3xl text-gray-900 text-center mb-10">How It Works — 3 Simple Steps</motion.h2>
            <div className="space-y-6">
              {page.howItWorks.map((step, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-5">
                  <div className="w-10 h-10 bg-[#0088CC] text-white rounded-full flex items-center justify-center flex-shrink-0 font-outfit font-bold text-sm">{i + 1}</div>
                  <p className="font-manrope text-base text-gray-700 pt-2">{step}</p>
                </motion.div>
              ))}
            </div>
            {/* Diagram */}
            <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
              <div className="bg-white border border-gray-200 rounded-xl px-5 py-3 flex items-center gap-2 shadow-sm">
                <Mail className="w-4 h-4 text-red-500" />
                <span className="font-outfit font-semibold text-sm text-gray-900">Email Alert</span>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-300 rotate-90 sm:rotate-0" />
              <div className="bg-[#0088CC]/5 border border-[#0088CC]/20 rounded-xl px-5 py-3 flex items-center gap-2 shadow-sm">
                <TelegramIcon className="w-4 h-4 text-[#0088CC]" />
                <span className="font-outfit font-semibold text-sm text-gray-900">Telegram</span>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-300 rotate-90 sm:rotate-0" />
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3 flex items-center gap-2 shadow-sm">
                <Users className="w-4 h-4 text-emerald-600" />
                <span className="font-outfit font-semibold text-sm text-gray-900">Team Responds</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Detailed Content */}
      <section className="py-16 sm:py-20 px-4 bg-[#F8F9FA]" data-testid="seo-detail">
        <div className="max-w-3xl mx-auto">
          <div
            className="prose prose-gray max-w-none font-manrope
              prose-headings:font-outfit prose-headings:font-bold
              prose-h2:text-2xl prose-h3:text-lg
              prose-a:text-[#0088CC] prose-a:no-underline hover:prose-a:underline"
            style={{ color: '#111827' }}
            dangerouslySetInnerHTML={{ __html: page.useCaseDetail }}
          />
        </div>
      </section>

      {/* Example Email Preview — unique per page */}
      {page.exampleEmail && (
        <section className="py-16 sm:py-20 px-4" data-testid="seo-example-email">
          <div className="max-w-3xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="font-manrope text-xs uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3 text-center">Example Alert</p>
              <h2 className="font-outfit font-bold text-2xl sm:text-3xl text-gray-900 text-center mb-10">This is what arrives in your Telegram</h2>
              <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
                {/* Email header bar */}
                <div className="bg-[#F8F9FA] border-b border-gray-200 px-5 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                  <span className="font-manrope text-xs text-gray-500 ml-2">Telegram → @instant_email_bot</span>
                </div>
                {/* Email body */}
                <div className="px-6 py-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full bg-[#0088CC]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-[#0088CC]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-manrope text-xs text-gray-400 truncate">From: {page.exampleEmail.from}</p>
                      <p className="font-outfit font-bold text-base text-gray-900 truncate mt-0.5">{page.exampleEmail.subject}</p>
                    </div>
                  </div>
                  <p className="font-manrope text-sm text-gray-700 leading-relaxed border-l-2 border-[#0088CC]/40 pl-4 mb-4">
                    {page.exampleEmail.preview}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 border-t border-gray-100">
                    <span className="font-manrope text-xs text-gray-400">Compatible with:</span>
                    <span className="font-manrope text-xs font-semibold text-gray-700">{page.exampleEmail.channels}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {page.testimonial && (
        <section className="py-16 sm:py-20 px-4 bg-[#F8F9FA]" data-testid="seo-testimonial">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="font-manrope text-lg sm:text-xl text-gray-700 italic leading-relaxed mb-4">"{page.testimonial.text}"</p>
              <p className="font-manrope text-sm font-semibold text-gray-400">— {page.testimonial.author}</p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Page-specific FAQ — unique per page */}
      {page.pageFaqs && page.pageFaqs.length > 0 && (
        <section className="py-16 sm:py-20 px-4" data-testid="seo-page-faqs">
          <div className="max-w-3xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="font-manrope text-xs uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3 text-center">Common Questions</p>
              <h2 className="font-outfit font-bold text-2xl sm:text-3xl text-gray-900 text-center mb-10">{page.headline} — FAQ</h2>
              <div className="space-y-3">
                {page.pageFaqs.map((f, i) => (
                  <details key={i} className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-all" data-testid={`seo-faq-${i}`}>
                    <summary className="font-outfit font-bold text-base text-gray-900 cursor-pointer list-none flex items-start justify-between gap-3">
                      <span>{f.q}</span>
                      <ArrowRight className="w-4 h-4 text-[#0088CC] flex-shrink-0 mt-1 group-open:rotate-90 transition-transform" />
                    </summary>
                    <p className="font-manrope text-sm text-gray-600 leading-relaxed mt-3 pl-0">{f.a}</p>
                  </details>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Pricing CTA */}
      <section className="py-16 sm:py-20 px-4 bg-gray-900" data-testid="seo-pricing-cta">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 bg-[#0088CC]/20 text-[#5FB8FF] font-manrope font-semibold text-xs uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              Simple Pricing
            </div>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-3">One-Time $4.99. Yours Forever.</h2>
            <p className="font-manrope text-gray-400 mb-2">Starter — permanent inbox</p>
            <p className="font-outfit font-black text-5xl sm:text-6xl text-white mb-1">$4.99<span className="text-2xl text-gray-500 font-normal"> one-time</span></p>
            <p className="font-manrope text-sm text-gray-500 mb-2">20 complimentary emails included · No credit card required to start</p>
            <p className="font-manrope text-xs text-gray-500 mb-8">Need unlimited emails + AI summaries? Upgrade to <span className="text-white font-semibold">Inbox Storage &amp; AI — $14.99/year</span></p>
            <a
              href={BOT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTA(`seo_pricing_${page.slug}`)}
              className="inline-flex items-center gap-2.5 bg-[#0088CC] text-white font-manrope font-bold px-10 py-4 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg text-base"
              data-testid="seo-pricing-cta-btn"
            >
              <TelegramIcon className="w-5 h-5" /> Get Started for $4.99
            </a>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 text-sm text-gray-500 font-manrope">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> No signup</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Permanent inbox</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Telegram groups</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-500" /> Privacy-first</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Pages */}
      {related.length > 0 && (
        <section className="py-16 sm:py-20 px-4 bg-[#F8F9FA]" data-testid="seo-related">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-outfit font-bold text-2xl text-gray-900 text-center mb-10">Related Use Cases</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((r, i) => (
                <Link key={i} to={`/use-cases/${r.slug}`} className="block bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md hover:border-gray-300 transition-all group" data-testid={`seo-related-${i}`}>
                  <p className="font-manrope text-xs text-gray-400 mb-2">{r.category}</p>
                  <h3 className="font-outfit font-bold text-base text-gray-900 group-hover:text-[#0088CC] transition-colors mb-2">{r.headline}</h3>
                  <p className="font-manrope text-sm text-gray-500 line-clamp-2">{r.subheadline}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-gray-300 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TelegramEmailBot" className="w-5 h-5 rounded object-cover" />
              <span className="font-outfit font-bold text-white text-sm">TelegramEmailBot</span>
            </div>
            <div className="flex items-center gap-6 font-manrope text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/use-cases" className="hover:text-white transition-colors">Use Cases</Link>
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <span className="font-manrope text-xs text-gray-300">&copy; 2026 TelegramEmailBot</span>
          </div>
          <FooterTrust variant="dark" />
        </div>
      </footer>

      {/* FAQ Schema */}
      <FAQSchema page={page} />
    </div>
  );
}

function SEOMeta({ page }) {
  React.useEffect(() => {
    document.title = page.metaTitle;
    const updateMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    updateMeta('description', page.metaDescription);
    updateMeta('keywords', page.keywords.join(', '));
    updateMeta('og:title', page.metaTitle, 'property');
    updateMeta('og:description', page.metaDescription, 'property');
    updateMeta('og:url', `https://telegramemailbot.com/use-cases/${page.slug}`, 'property');
    const link = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
    link.href = `https://telegramemailbot.com/use-cases/${page.slug}`;
  }, [page]);
  return null;
}

function FAQSchema({ page }) {
  // Prefer page-specific FAQs (unique per use case) for richer + non-duplicate schema
  const faqs = (page.pageFaqs && page.pageFaqs.length > 0)
    ? page.pageFaqs
    : [
        { q: `How do I set up ${page.headline.toLowerCase()}?`, a: page.howItWorks.join(' ') },
        { q: "Is there a free trial?", a: "No trial period — the Starter Email is $4.99 one-time, giving you a permanent inbox with 20 complimentary emails. If you need unlimited emails plus AI summaries, upgrade to Inbox Storage & AI at $14.99/year." },
        { q: "Does it work with Telegram groups?", a: "Yes. Add the bot to any Telegram group and the entire team receives email alerts instantly." },
        { q: "How fast do alerts arrive?", a: "Emails typically arrive in Telegram within 30-60 seconds of being sent." },
      ];
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}
